import { useCallback, useEffect, useState } from "react";
import { doc, collection, onSnapshot, Firestore } from "firebase/firestore";
import { Player, RoundSolve } from "../components/utils/MatchUtils";

interface UsePlayerMatchResult {
  player: Player | null,
  roundSolves: RoundSolve[],
  loading: boolean,
  error: Error | null
}

/**
 * Custom hook to fetch and listen for real-time updates for a specific player's data and their round solves.
 *
 * @param db - Firestore instance
 * @param matchId - ID of the match document
 * @param playerId - ID of the player
 * @returns Real-time player data and round solves, along with loading and error states
 */
export const usePlayerMatch = (
  db: Firestore,
  matchId: string,
  playerId: string | null
): UsePlayerMatchResult => {
  const [player, setPlayer] = useState<Player | null>(null);
  const [roundSolves, setRoundSolves] = useState<RoundSolve[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!matchId || !playerId) {
      //setError(new Error("Match ID and Player ID are required."));
      //setLoading(false);
      return;
    }

    const playerRef = doc(db, "matches", matchId, "players", playerId);
    const roundSolvesRef = collection(playerRef, "round-solves");

    let unsubscribePlayer: () => void;
    let unsubscribeRoundSolves: () => void;

    try {
      // Listen for real-time updates to the player's data
      unsubscribePlayer = onSnapshot(
        playerRef,
        (playerSnap) => {
          if (playerSnap.exists()) {
            setPlayer(playerSnap.data() as Player);
          } else {
            setPlayer(null);
          }
          setLoading(false);
        },
        (error) => {
          setError(error);
          setLoading(false);
        }
      );

      // Listen for real-time updates to the player's round solves
      unsubscribeRoundSolves = onSnapshot(
        roundSolvesRef,
        (roundSolvesSnap) => {
          const solves: RoundSolve[] = roundSolvesSnap.docs.map((doc) => ({
            roundNumber: doc.data().roundNumber,
            solveNumber: doc.data().solveNumber,
            solveTime: doc.data().solveTime,
            scramble: doc.data().scramble,
            id: doc.id,
            status: doc.data().status
          }));
          setRoundSolves(solves);
        },
        (error) => {
          setError(error);
        }
      );
    } catch (err) {
      setError(err as Error);
      setLoading(false);
    }

    return () => {
      if (unsubscribePlayer) unsubscribePlayer();
      if (unsubscribeRoundSolves) unsubscribeRoundSolves();
    };
  }, [db, matchId, playerId]);

    const getStatus = (solveTime: number | null)=>{
        
        // If the solve exists but doesn't have a time, it's Waiting
        if (solveTime === undefined || solveTime === null) {
          return "Waiting";
        }
      
        // If the solve has a time, it's Solved
        return "Solved";
        

    }

  return { player, roundSolves, loading, error };
};
